import { type Database } from '@databases/model/database';
import { type Field, FieldType, NumberFormat } from '@databases/model/field';
import { unparse } from 'papaparse';

import { type Warnings } from './warnings';

const CURRENCY_SYMBOL_TO_NAME: Record<string, string> = {
  $: 'us_dollar',
  '€': 'euro',
  '£': 'pound',
  '¥': 'yen',
  A$: 'australian_dollar',
};

const SUPPORTED_DEPENDENT_FIELD_TYPES = new Set([
  FieldType.PageDetails,
  FieldType.PageStatus,
  FieldType.PageLabels,
]);

const UNSUPPORTED_DEPENDENT_FIELD_TYPES = new Set([
  FieldType.EntryDetails,
  FieldType.JiraIssueDetails,
]);

function serializeFieldConfiguration(
  field: Field,
  database: Database,
  warnings: Warnings
): string {
  if (field.fieldId) {
    if (SUPPORTED_DEPENDENT_FIELD_TYPES.has(field.type)) {
      const dependentField = database.fieldsById[field.fieldId];
      if (dependentField) {
        return `depends_on:'${dependentField.name}'`;
      }
    } else if (UNSUPPORTED_DEPENDENT_FIELD_TYPES.has(field.type)) {
      warnings.add(
        `Unsupported dependent field type: ${field.type} for field "${field.name}". Configuration will be empty.`
      );
      return '';
    }
  }

  if (field.type !== FieldType.Number) {
    return '';
  }

  const numberFormat = field.numberFormat;
  const customFormat = field.customNumberFormat;

  switch (numberFormat) {
    case NumberFormat.NUMBER:
      return 'number_format:number';
    case NumberFormat.PERCENT:
      return 'number_format:percent';
    case NumberFormat.CURRENCY:
    case NumberFormat.DOLLAR:
    case NumberFormat.EURO: {
      const prefix = customFormat.prefix;
      if (prefix) {
        const currencyName = CURRENCY_SYMBOL_TO_NAME[prefix];
        if (currencyName) {
          return `number_format:currency_${currencyName}`;
        }
      }
      return 'number_format:currency_us_dollar';
    }
    default:
      return '';
  }
}

function serializeFieldType(fieldType: FieldType): string | undefined {
  switch (fieldType) {
    case FieldType.Text:
      return 'text';
    case FieldType.Number:
      return 'number';
    case FieldType.Date:
      return 'date';
    case FieldType.Select:
      return 'tag';
    case FieldType.User:
      return 'user';
    case FieldType.PageLink:
      return 'confluence-page-link';
    case FieldType.Hyperlink:
      return 'hyperlink';
    case FieldType.JiraIssue:
      return 'jira-work-item';
    case FieldType.JiraIssueDetails:
      return 'jira-work-item-details';
    case FieldType.PageStatus:
      return 'confluence-page-status';
    case FieldType.PageLabels:
      return 'confluence-page-labels';
    case FieldType.PageDetails:
      return 'confluence-page-details';
    // These are not supported by the serializer yet
    case FieldType.Attachment:
      return 'attachment';
    case FieldType.EntryLink:
      return 'database-entry-link';
    case FieldType.EntryDetails:
      return 'database-entry-details';
    case FieldType.Backlink:
      return 'backlink';
    default:
      return;
  }
}

/**
 * Serializes the schema of a database.
 *
 * This will serialize as a CSV with the following format:
 *
 * field_name,type,depends_on,configuration
 * Field 1,text,,
 * Field 2,number,,"number_format:percent"
 * Field 3,date,,
 * Field 4,number,,"number_format:currency_yen"
 *
 * Where the configuration field is populated for fields that have special configuration:
 * - Number format: "number_format:percent", "number_format:currency_us_dollar", etc.
 * - Field dependency: "depends_on:'<field_name>'" for dependent field types
 *
 * @param database The database to serialize the schema of.
 * @returns The serialized schema.
 */
export function serializeDatabaseSchema(database: Database, warnings: Warnings): string {
  const schema = database.fields
    .map((fieldId) => {
      const field = database.fieldsById[fieldId];
      const type = serializeFieldType(field.type);

      if (!type) {
        warnings.add(`Unsupported field type: ${field.type} for field ${fieldId}`);
        return undefined;
      }

      return {
        field_name: field.name,
        type,
        depends_on: '',
        configuration: serializeFieldConfiguration(field, database, warnings),
      };
    })
    .filter((e) => e !== undefined);

  return unparse(schema, {
    header: true,
    escapeChar: '\\',
    columns: ['field_name', 'type', 'depends_on', 'configuration'],
  });
}
